import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

import { convertNumber } from 'helpers/utils';
import { useDebounce } from 'hooks/useDebounce';
import { clientApi } from 'helpers/api/client';

import { Client } from 'store/client/actions';
import { clientListSelector } from 'store/client/selectors';

import { Portal } from 'components/Portal';
import { Icon } from 'components/Icon';
import { Loader } from 'components/Loader';
import { ModalCreateClient } from 'components/ModalCreateClient';
import { InputIcon } from 'components/Input';
import { ButtonIcon, ButtonLabel } from 'components/Button';
import ReactTooltip from 'react-tooltip';

import './style.scss';
import { errorToast } from 'configuration/notifications';
import { ModalDiia } from 'components/ModalDiia';

const allLetters = /[a-zA-Zа-яА-ЯєЄіІЇї]/;

const ModalClient = ({
  historyParams,
  selected,
  disableSelectable,
  onSubmit,
  onClose,
  searchString,
  isFromMainPage,
}) => {
  const history = useHistory();
  const dispatch = useDispatch();

  const clientList = useSelector(clientListSelector);
  const [modalDiia, setModalDiia] = useState(false);

  const [createClientModal, setCreateClientModal] = useState(false);
  const [type, setType] = useState('individ');
  const [pending, setPending] = useState(false);
  const [search, setSearch] = useState(searchString);
  const [selectedUser, setSelectedUser] = useState(selected);

  const debouncedSearch = useDebounce(search, 500, true);

  useEffect(() => {
    clientsList(type);
  }, [debouncedSearch]);

  useEffect(() => {
    ReactTooltip.rebuild();
  });

  const values = useCallback(() => {
    return allLetters.test(search) ? { FIO: search } : { PhMain: search };
  }, [search]);

  const clientsList = (clientsType) => {
    const query = {};
    query.limit = !search ? 10 : 100;

    if (clientsType === 'individ') {
      query.individual = true;
      query.search = search;
    } else if (clientsType === 'legal') {
      history.push({ search: '' });
      query.individual = false;
      query.search = search;
    }

    dispatch(Client.getClientList(query));
  };

  const handleCreateClient = async (data) => {
    if (pending) {
      return;
    }
    data.individual = type === 'individ';
    setPending(true);

    const { data: client } = await clientApi.createClient(data);
    handleSelectUser(client);
    dispatch(Client.addCreatedClientToList(client));
    setPending(false);
    return client;
  };

  const handleSearch = (value) => {
    const params = new URLSearchParams();
    if (value) {
      params.set('search', value);
    } else {
      params.delete('search');
    }
    setSearch(value);
    history.push({ search: params.toString() });
  };

  const handleType = (newType) => {
    setType(newType);
    setSearch('');
    clientsList(newType);
  };

  const handleSelectUser = (client) => {
    if (!disableSelectable && client.isPendingDeletion) {
      errorToast('Данный клиент помечен на удаление!');
    }
    return !disableSelectable && !client.isPendingDeletion && setSelectedUser(client);
  };

  const handleClient = (client) => {
    history.push(`/client/${client.id}`, historyParams);
  };

  const handleSubmitUser = () => {
    return !disableSelectable && selectedUser && onSubmit(selectedUser);
  };

  const toggleCreateClientModal = (toggle) => {
    setCreateClientModal(toggle);
  };

  const toggleModalDiia = (toggle) => {
    return () => {
      setModalDiia(toggle);
    };
  };

  if (modalDiia) {
    return <ModalDiia onClose={toggleModalDiia(false)} historyParams={historyParams} />;
  }

  if (createClientModal) {
    return (
      <ModalCreateClient
        onSubmit={handleCreateClient}
        pending={pending}
        onClose={() => toggleCreateClientModal(false)}
        type={type}
        defaultValues={values()}
        isFromMainPage={isFromMainPage}
        historyParams={historyParams}
      />
    );
  }

  return (
    <Portal id="modal-client" className="contragent" onClose={onClose}>
      <div className="modal-title">
        <div className="header-title">Контрагенты</div>

        <ButtonLabel onClick={() => handleType('individ')} active={type === 'individ'} text="Физ. лица" />

        <ButtonLabel onClick={() => handleType('legal')} active={type === 'legal'} text="Юр. лица" />
      </div>

      <InputIcon
        icon="search"
        onChange={handleSearch}
        value={search}
        placeholder={type === 'individ' ? 'ФИО или номер' : 'Наименование или ЕДРПОУ'}
      />

      <div className="contragent-list dropdownScroll">
        {clientList &&
          clientList.map((client) => (
            <div
              key={client.id}
              className={`contragent-item ${selectedUser?.id === client.id ? 'is-selected' : ''}`}
              onClick={() => handleSelectUser(client)}
            >
              <div>
                <div className="contragent-select">
                  <Icon
                    name={
                      client.isPendingDeletion
                        ? 'userverify-pending-deletition'
                        : client.isTemporary
                        ? 'userverify-yellow'
                        : 'userverify'
                    }
                  />
                  <div className="contragent-data">{client.FIO}</div>
                </div>

                {type === 'individ' && (
                  <div className="contragent-select">
                    <Icon name="modalphone" />
                    <div className="contragent-data">{convertNumber(client.PhMain)}</div>
                  </div>
                )}

                {type === 'legal' && (client.EDRPOU || client.INN) && (
                  <div className="contragent-select">
                    <Icon name="hashtag" />
                    <div className="contragent-data">{client.EDRPOU || client.INN}</div>
                  </div>
                )}

                {client.PhAdd && (
                  <div className="contragent-select">
                    <Icon name="modalphone" />
                    <div className="contragent-data">{convertNumber(client.PhAdd)}</div>
                  </div>
                )}
              </div>

              <div
                className="button-case"
                onClick={() => handleClient(client)}
                data-tip
                data-for="client-profile-tooltip"
              >
                <Icon name="usercard" />
              </div>
            </div>
          ))}

        {clientList === null && <Loader type="small" color="#000" />}
      </div>

      <div className="flex-center">
        {!disableSelectable && (
          <ButtonIcon
            className="btn-modal btn-client-modal"
            onClick={handleSubmitUser}
            disabled={selectedUser === null || disableSelectable}
            icon="client-btn"
            text="Выбрать клиента"
          />
        )}
        <div data-tip data-for="client-create-tooltip">
          <ButtonIcon
            className="btn-modal btn-contragent"
            onClick={() => toggleCreateClientModal(true)}
            icon="addclient-btn"
          />
        </div>
        {type === 'individ' && <ButtonIcon icon="diia" onClick={toggleModalDiia(true)} />}
        <ReactTooltip place="bottom" id="client-create-tooltip">
          Создать клиенка
        </ReactTooltip>
        <ReactTooltip place="left" id="client-profile-tooltip">
          Профиль клиенка
        </ReactTooltip>
      </div>
    </Portal>
  );
};

ModalClient.propTypes = {
  selected: PropTypes.object,
  disableSelectable: PropTypes.bool,
  historyParams: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func,
  searchString: PropTypes.string,
  isFromMainPage: PropTypes.bool,
};

ModalClient.defaultProps = {
  selected: null,
  historyParams: {},
  disableSelectable: false,
  onSubmit: () => {},
  isFromMainPage: false,
  searchString: '',
};

export { ModalClient };
